import { movies } from "@/mocks/movies";
import { tvShows } from "@/mocks/tv";
import { newsList } from "@/mocks/news";
import type { MediaItem, NewsItem, SearchResult } from "@/types/media";

export { movies, tvShows, newsList };

export const REGION_OPTIONS = ["全部", "中国", "中国香港", "美国", "英国", "日本", "韩国", "法国"];
export const MOVIE_GENRE_OPTIONS = [
  "全部", "科幻", "动作", "悬疑", "剧情", "爱情", "恐怖", "武侠", "喜剧", "冒险", "犯罪", "奇幻",
];
export const TV_GENRE_OPTIONS = [
  "全部", "历史", "悬疑", "都市", "犯罪", "科幻", "职场", "奇幻", "武侠", "家族", "青春", "生活", "动作",
];
export const YEAR_OPTIONS = ["全部", "2026", "2025", "2024"];
export const SORT_OPTIONS = [
  { value: "updated", label: "最新更新" },
  { value: "popularity", label: "按热度" },
  { value: "rating", label: "按评分" },
];

export function getAllMedia(): MediaItem[] {
  return [...movies, ...tvShows];
}

export function getMediaListByType(type: "movie" | "tv"): MediaItem[] {
  return type === "movie" ? movies : tvShows;
}

export function getMediaById(id: string): MediaItem | undefined {
  return getAllMedia().find((item) => item.id === id);
}

export function getNewsById(id: string): NewsItem | undefined {
  return newsList.find((item) => item.id === id);
}

export function getLatestMovies(count?: number): MediaItem[] {
  const list = [...movies].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return typeof count === "number" ? list.slice(0, count) : list;
}

export function getHotTvShows(count?: number): MediaItem[] {
  const list = [...tvShows].sort((a, b) => b.popularity - a.popularity);
  return typeof count === "number" ? list.slice(0, count) : list;
}

export function getFeaturedMedia(count = 5): MediaItem[] {
  return [...getAllMedia()].sort((a, b) => b.popularity - a.popularity).slice(0, count);
}

export function getLatestNews(count?: number): NewsItem[] {
  const list = [...newsList].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  return typeof count === "number" ? list.slice(0, count) : list;
}

export function getRelatedMedia(item: MediaItem, count = 4): MediaItem[] {
  return getAllMedia()
    .filter((other) => other.id !== item.id)
    .map((other) => {
      const genreOverlap = other.genres.filter((g) => item.genres.includes(g)).length;
      const sameRegion = other.region === item.region ? 1 : 0;
      const sameType = other.type === item.type ? 1 : 0;
      return { other, score: genreOverlap * 3 + sameRegion * 2 + sameType };
    })
    .sort((a, b) => b.score - a.score || b.other.popularity - a.other.popularity)
    .slice(0, count)
    .map((entry) => entry.other);
}

export function getRelatedNews(item: NewsItem, count = 3): NewsItem[] {
  return newsList
    .filter((other) => other.id !== item.id)
    .map((other) => {
      const tagOverlap = other.tags.filter((t) => item.tags.includes(t)).length;
      const sameCategory = other.category === item.category ? 2 : 0;
      return { other, score: tagOverlap * 3 + sameCategory };
    })
    .sort((a, b) => b.score - a.score || b.other.publishedAt.localeCompare(a.other.publishedAt))
    .slice(0, count)
    .map((entry) => entry.other);
}

export function searchAll(keyword: string): SearchResult[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  const results: SearchResult[] = [];
  getAllMedia().forEach((item) => {
    const haystack = [item.title, item.director, ...item.cast, ...item.genres, item.summary]
      .join(" ")
      .toLowerCase();
    if (haystack.includes(kw)) {
      results.push({
        id: item.id,
        kind: "media",
        title: item.title,
        summary: item.summary,
        cover: item.cover,
        meta: `${item.year} · ${item.region} · ${item.genres.join(" / ")}`,
        path: `/detail/${item.id}`,
        order: item.popularity,
        date: item.updatedAt,
      });
    }
  });
  newsList.forEach((item) => {
    const haystack = [item.title, item.summary, item.author, ...item.tags].join(" ").toLowerCase();
    if (haystack.includes(kw)) {
      results.push({
        id: item.id,
        kind: "news",
        title: item.title,
        summary: item.summary,
        cover: item.cover,
        meta: `${item.category} · ${item.publishedAt}`,
        path: `/news/${item.id}`,
        order: Number(item.publishedAt.replace(/-/g, "")),
        date: item.publishedAt,
      });
    }
  });
  return results;
}